import React, { useRef, useEffect } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Navbar, { INavBarProps } from 'tbk-components/src/components/NavBar';
import Nav, { NavItem } from 'tbk-components/src/components/Nav';
import MainNavMobile, { IMainNavMobileProps } from './Mobile/MainNavMobile';

export interface IDesktopCalloutMenuLink {
	image?: IImage;
	link?: ILink;
}
export interface IHeaderProps extends IBaseElementProps {
	children?: any;
	logo?: IImage;
	logoURL?: string;
	taglineText?: string;
	taglineLink?: ILink;
	secondaryNav?: NavItem[];
	mainNav?: NavItem[];
	desktopCalloutText?: string;
	desktopCalloutMenuLinks?: IDesktopCalloutMenuLink[];
	desktopCalloutFeatureLink?: ILink;
	mobile?: IMainNavMobileProps;
	homeURL?: string;
}

/**
 * Header
 * @block
 * @icon align-full-width
 */
const Header: React.FC<IHeaderProps> = ({
	children,
	className,
	classNames = [],
	logo,
	logoURL,
	taglineText,
	taglineLink,
	secondaryNav,
	mainNav,
	desktopCalloutText,
	desktopCalloutMenuLinks,
	desktopCalloutFeatureLink,
	mobile = {},
	homeURL,
}) => {
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		let desktopMainMenu = ref.current.querySelector(
			'.desktop-main-menu',
		) as HTMLElement;

		let dropDownToggles = ref.current.querySelectorAll(
			'.desktop-main-menu .dropdown-toggle',
		) as NodeListOf<HTMLAnchorElement>;

		let dropDownToggleArray = Array.from(dropDownToggles);

		let showClassCheck = function () {
			function checkShow(toggle) {
				return !toggle.classList.contains('show');
			}
			return dropDownToggleArray.every(checkShow);
		};

		dropDownToggles.forEach((item) => {
			item.addEventListener('shown.bs.dropdown', (e) => {
				e.preventDefault();
				if (!showClassCheck()) {
					setTimeout(function () {
						desktopMainMenu.classList.add('mega-menu-open');
					}, 400);
				}
			});
			item.addEventListener('hidden.bs.dropdown', (e) => {
				e.preventDefault();
				if (showClassCheck()) {
					desktopMainMenu.classList.remove('mega-menu-open');
				}
			});
		});
	}, []);

	return (
		<>
			<Navbar
				className="desktop-header navbar d-none d-lg-block navbar-expand-lg navbar-light bg-white"
				logo={logo}
				logoURL={logoURL}
			>
				<div className="desktop-header__container" ref={ref}>
					{(taglineText || (taglineLink && taglineLink.href)) && (
						<div className="desktop-header__left">
							<div className="desktop-header__tagline">
								<p>
									{taglineText && (
										<span className="desktop-header__tagline-text">
											{taglineText}{' '}
										</span>
									)}
									{taglineLink && taglineLink.href && (
										<a
											href={taglineLink.href}
											className="desktop-header__tagline-link"
										>
											{taglineLink.label}
										</a>
									)}
								</p>
							</div>
						</div>
					)}
					<div className="desktop-header__right">
						<div className="desktop-header__right-container">
							<div className="desktop-header__right-top">
								<form
									className="desktop-header__search d-flex"
									action={homeURL}
									method="get"
								>
									<input
										name="s"
										className="form-control"
										type="text"
										placeholder="Search..."
										aria-label="Search"
									/>
									<button className="btn" type="submit">
										<i className="icon-search" />
										<span className="visually-hidden">
											Search
										</span>
									</button>
								</form>
								{secondaryNav && (
									<Nav
										navItems={secondaryNav}
										collapseEnabled={false}
										dropdownEnabled={false}
										classNames={['desktop-secondary-menu']}
									/>
								)}
							</div>
							<div className="desktop-header__right-bottom">
								{mainNav && (
									<Nav
										navItems={mainNav}
										collapseEnabled={false}
										dropdownEnabled={true}
										classNames={['desktop-main-menu']}
									/>
								)}
							</div>
						</div>

						{desktopCalloutText && (
							<div className="desktop-header__callout dropdown">
								<a
									className="desktop-header__callout-link"
									href="#"
									role="button"
									id="desktop-header__callout-link"
									data-bs-toggle="dropdown"
									aria-expanded="false"
								>
									{desktopCalloutText}
								</a>
								{desktopCalloutMenuLinks.length > 0 && (
									<ul
										className="desktop-header__callout-dropdown-menu dropdown-menu dropdown-menu-end"
										aria-labelledby="desktop-header__callout-link"
									>
										{desktopCalloutMenuLinks.map(
											(calloutLink, index) => (
												<li key={index}>
													<a
														className="dropdown-item"
														href={
															calloutLink.link
																.href
														}
													>
														<Image
															{...calloutLink.image}
														/>
														{calloutLink.link.label}
													</a>
												</li>
											),
										)}
										{desktopCalloutFeatureLink && (
											<li className="desktop-header__callout-featured">
												<a
													className="desktop-header__callout-feature-link dropdown-item"
													href={
														desktopCalloutFeatureLink.href
													}
												>
													{
														desktopCalloutFeatureLink.label
													}
												</a>
											</li>
										)}
									</ul>
								)}
							</div>
						)}
					</div>
				</div>
			</Navbar>
			<div className="d-lg-none">
				<MainNavMobile {...mobile} />
			</div>
		</>
	);
};

export default Header;
